import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../../components/Sidebar';
import ProfileDropdown from '../../components/ProfileDropdown';
import axios from 'axios';

import API_BASE from '../../utils/api';
const API = API_BASE;

const examTypes = [
  { value: 'unit_test', label: 'Unit Test' },
  { value: 'mid_term', label: 'Mid Term' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'half_yearly', label: 'Half Yearly' },
  { value: 'annual', label: 'Annual' },
  { value: 'assignment', label: 'Assignment' },
];

const Exams = () => {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('exams');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [myClass, setMyClass] = useState(null);
  const [selectedExam, setSelectedExam] = useState(null);
  const [students, setStudents] = useState([]);
  const [marksData, setMarksData] = useState([]);

  const [form, setForm] = useState({
    examName: '', examType: 'unit_test', subject: '',
    date: '', totalMarks: '', passingMarks: ''
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchMyClass = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/classes/my-class`, { headers });
      setMyClass(res.data);
    } catch (err) {
      console.error('No class assigned:', err);
    }
  }, []);

  const fetchExams = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/exams`, { headers });
      setExams(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchMyClass(); fetchExams(); }, [fetchMyClass, fetchExams]);

  const showMsg = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleCreateExam = async (e) => {
    e.preventDefault();
    if (!myClass) return showMsg('error', 'No class assigned to you');
    try {
      const payload = { ...form, classId: myClass._id, totalMarks: Number(form.totalMarks), passingMarks: Number(form.passingMarks) };
      await axios.post(`${API}/exams`, payload, { headers });
      showMsg('success', 'Exam created successfully!');
      setShowCreateForm(false);
      setForm({ examName: '', examType: 'unit_test', subject: '', date: '', totalMarks: '', passingMarks: '' });
      fetchExams();
    } catch (err) {
      showMsg('error', err.response?.data?.error || 'Failed to create exam');
    }
  };

  const handleDeleteExam = async (id) => {
    if (!window.confirm('Delete this exam?')) return;
    try {
      await axios.delete(`${API}/exams/${id}`, { headers });
      showMsg('success', 'Exam deleted');
      fetchExams();
    } catch (err) {
      showMsg('error', err.response?.data?.error || 'Cannot delete');
    }
  };

  const loadStudentsForMarks = async (exam) => {
    setSelectedExam(exam);
    setActiveTab('marks');
    try {
      const res = await axios.get(`${API}/exams/${exam._id}/students`, { headers });
      const studentsList = res.data.students || [];
      setStudents(studentsList);
      setMarksData(studentsList.map(s => ({
        studentId: s.studentId,
        marksObtained: s.marksObtained !== undefined && s.marksObtained !== '' ? s.marksObtained : '',
        remarks: s.remarks || ''
      })));
    } catch (err) {
      console.error(err);
      showMsg('error', 'Failed to load students');
    }
  };

  const updateMark = (index, field, value) => {
    const updated = [...marksData];
    updated[index] = { ...updated[index], [field]: value };
    setMarksData(updated);
  };

  const allMarksEntered = () => {
    return marksData.length > 0 && marksData.every(m => m.marksObtained !== '' && m.marksObtained !== null && m.marksObtained !== undefined);
  };

  const handleSaveMarks = async () => {
    if (!allMarksEntered()) {
      return showMsg('error', 'Please enter marks for ALL students before saving');
    }
    try {
      setLoading(true);
      await axios.post(`${API}/exams/${selectedExam._id}/marks`, { marks: marksData }, { headers });
      showMsg('success', 'Marks saved as draft!');
      loadStudentsForMarks(selectedExam);
    } catch (err) {
      showMsg('error', err.response?.data?.error || 'Failed to save marks');
    } finally { setLoading(false); }
  };

  const handleSubmitForReview = async () => {
    if (!window.confirm('Submit marks for principal review? You cannot edit after submission.')) return;
    try {
      await axios.put(`${API}/exams/${selectedExam._id}/submit`, {}, { headers });
      showMsg('success', 'Marks submitted for principal review!');
      loadStudentsForMarks(selectedExam);
    } catch (err) {
      showMsg('error', err.response?.data?.error || 'Failed to submit');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-700';
      case 'submitted': return 'bg-blue-100 text-blue-700';
      case 'approved': return 'bg-green-100 text-green-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const calcGrade = (marks, total) => {
    if (!marks && marks !== 0) return null;
    const pct = (parseFloat(marks) / total) * 100;
    if (pct >= 90) return { grade: 'A+', color: 'text-green-600' };
    if (pct >= 80) return { grade: 'A', color: 'text-green-600' };
    if (pct >= 70) return { grade: 'B+', color: 'text-blue-600' };
    if (pct >= 60) return { grade: 'B', color: 'text-blue-600' };
    if (pct >= 50) return { grade: 'C', color: 'text-yellow-600' };
    if (pct >= 40) return { grade: 'D', color: 'text-orange-600' };
    return { grade: 'F', color: 'text-red-600' };
  };

  const subjects = myClass?.subjects || [];

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Exams & Marks</h1>
              <p className="text-gray-500 mt-1">
                {myClass ? `Class: ${myClass.className} - ${myClass.section}` : 'No class assigned'}
              </p>
            </div>
            <ProfileDropdown />
          </div>

          {message.text && (
            <div className={`mb-4 p-4 rounded-lg ${message.type === 'success' ? 'bg-green-100 text-green-800 border border-green-300' : 'bg-red-100 text-red-800 border border-red-300'}`}>
              {message.text}
            </div>
          )}

          {!myClass && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
              <p className="text-yellow-700 text-lg font-medium">No class is assigned to you yet.</p>
              <p className="text-yellow-600 mt-1">Please contact the admin to assign a class.</p>
            </div>
          )}

          {myClass && (
            <>
              <div className="flex space-x-1 bg-gray-200 rounded-lg p-1 mb-6 w-fit">
                <button onClick={() => { setActiveTab('exams'); setSelectedExam(null); }}
                  className={`px-6 py-2 rounded-md font-medium transition-colors ${activeTab === 'exams' ? 'bg-white text-indigo-700 shadow' : 'text-gray-600 hover:text-gray-800'}`}>
                  My Exams
                </button>
                {selectedExam && (
                  <button onClick={() => setActiveTab('marks')}
                    className={`px-6 py-2 rounded-md font-medium transition-colors ${activeTab === 'marks' ? 'bg-white text-indigo-700 shadow' : 'text-gray-600 hover:text-gray-800'}`}>
                    Enter Marks
                  </button>
                )}
              </div>

              {activeTab === 'exams' && (
                <div>
                  <button onClick={() => setShowCreateForm(!showCreateForm)}
                    className="mb-4 bg-indigo-600 text-white px-5 py-2 rounded-lg hover:bg-indigo-700 transition-colors">
                    {showCreateForm ? 'Cancel' : '+ Create Exam'}
                  </button>

                  {showCreateForm && (
                    <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                      <h2 className="text-lg font-semibold mb-4">Create New Exam</h2>
                      <div className="bg-indigo-50 rounded-lg p-3 mb-4 text-sm text-indigo-700">
                        Class: <strong>{myClass.className} - {myClass.section}</strong> (auto-assigned)
                      </div>
                      <form onSubmit={handleCreateExam} className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Exam Name *</label>
                          <input type="text" value={form.examName} onChange={(e) => setForm({ ...form, examName: e.target.value })}
                            className="w-full border rounded-lg px-3 py-2" placeholder="e.g. Unit Test 1" required />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                          <select value={form.examType} onChange={(e) => setForm({ ...form, examType: e.target.value })}
                            className="w-full border rounded-lg px-3 py-2">
                            {examTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
                          <select value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                            className="w-full border rounded-lg px-3 py-2" required>
                            <option value="">Select Subject</option>
                            {subjects.map((sub, idx) => (
                              <option key={idx} value={sub.subjectName}>{sub.subjectName}</option>
                            ))}
                          </select>
                          {subjects.length === 0 && (
                            <p className="text-xs text-red-500 mt-1">No subjects found. Ask admin/principal to add subjects.</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                          <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                            className="w-full border rounded-lg px-3 py-2" required />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Total Marks *</label>
                          <input type="number" value={form.totalMarks} onChange={(e) => setForm({ ...form, totalMarks: e.target.value })}
                            className="w-full border rounded-lg px-3 py-2" min="1" required />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Passing Marks *</label>
                          <input type="number" value={form.passingMarks} onChange={(e) => setForm({ ...form, passingMarks: e.target.value })}
                            className="w-full border rounded-lg px-3 py-2" min="1" required />
                        </div>
                        <div className="flex items-end">
                          <button type="submit" className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 w-full">
                            Create Exam
                          </button>
                        </div>
                      </form>
                    </div>
                  )}

                  {loading ? <p className="text-center py-10 text-gray-500">Loading...</p> : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {exams.length === 0 ? (
                        <div className="col-span-full text-center py-10 text-gray-400">
                          <p>No exams created yet. Click "+ Create Exam" to get started.</p>
                        </div>
                      ) : exams.map(exam => (
                        <div key={exam._id} className="bg-white rounded-xl shadow p-5 border-l-4 border-indigo-500 hover:shadow-lg transition-shadow">
                          <div className="flex justify-between items-start mb-3">
                            <h3 className="font-bold text-gray-800 text-lg">{exam.examName}</h3>
                            <span className="bg-indigo-100 text-indigo-700 text-xs px-2 py-1 rounded-full font-medium">
                              {examTypes.find(t => t.value === exam.examType)?.label}
                            </span>
                          </div>
                          <div className="space-y-1 text-sm text-gray-600 mb-4">
                            <p>Subject: {exam.subject}</p>
                            <p>Class: {exam.classId?.className} - {exam.classId?.section}</p>
                            <p>Date: {new Date(exam.date).toLocaleDateString()}</p>
                            <p>Total: {exam.totalMarks} | Pass: {exam.passingMarks}</p>
                          </div>
                          <div className="flex gap-2">
                            <button onClick={() => loadStudentsForMarks(exam)}
                              className="flex-1 bg-indigo-600 text-white py-2 rounded-lg text-sm hover:bg-indigo-700 transition-colors">
                              Enter Marks
                            </button>
                            <button onClick={() => handleDeleteExam(exam._id)}
                              className="px-3 py-2 border border-red-300 text-red-500 rounded-lg text-sm hover:bg-red-50">
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'marks' && selectedExam && (
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <div className="flex justify-between items-center mb-2">
                    <div>
                      <h2 className="text-xl font-bold text-gray-800">{selectedExam.examName}</h2>
                      <p className="text-sm text-gray-500">
                        {selectedExam.subject} | {selectedExam.classId?.className} - {selectedExam.classId?.section} | Total: {selectedExam.totalMarks} | Pass: {selectedExam.passingMarks}
                      </p>
                    </div>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-800">
                    Enter marks for <strong>every student</strong> in the class. All marks are required before saving.
                  </div>

                  <div className="flex gap-4 mb-4 text-sm">
                    <span className="bg-blue-50 text-blue-700 px-3 py-1 rounded-lg">
                      Total Students: <strong>{students.length}</strong>
                    </span>
                    <span className="bg-green-50 text-green-700 px-3 py-1 rounded-lg">
                      Marks Entered: <strong>{marksData.filter(m => m.marksObtained !== '' && m.marksObtained !== undefined).length}</strong>
                    </span>
                    <span className="bg-red-50 text-red-700 px-3 py-1 rounded-lg">
                      Remaining: <strong>{marksData.filter(m => m.marksObtained === '' || m.marksObtained === undefined).length}</strong>
                    </span>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="bg-gray-50 border-b">
                          <th className="p-3 text-sm font-semibold text-gray-600">#</th>
                          <th className="p-3 text-sm font-semibold text-gray-600">Roll No</th>
                          <th className="p-3 text-sm font-semibold text-gray-600">Student Name</th>
                          <th className="p-3 text-sm font-semibold text-gray-600">Marks (/{selectedExam.totalMarks})</th>
                          <th className="p-3 text-sm font-semibold text-gray-600">Grade</th>
                          <th className="p-3 text-sm font-semibold text-gray-600">Pass/Fail</th>
                          <th className="p-3 text-sm font-semibold text-gray-600">Remarks</th>
                          <th className="p-3 text-sm font-semibold text-gray-600">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {students.map((s, i) => {
                          const markVal = marksData[i]?.marksObtained;
                          const hasMarks = markVal !== '' && markVal !== undefined && markVal !== null;
                          const gradeInfo = hasMarks ? calcGrade(markVal, selectedExam.totalMarks) : null;
                          const isPassed = hasMarks && parseFloat(markVal) >= selectedExam.passingMarks;
                          const isEditable = s.status !== 'submitted' && s.status !== 'approved';
                          const isEmpty = !hasMarks && isEditable;

                          return (
                            <tr key={s.studentId} className={`border-b hover:bg-gray-50 ${isEmpty ? 'bg-red-50' : ''}`}>
                              <td className="p-3 text-sm">{i + 1}</td>
                              <td className="p-3 text-sm font-medium">{s.rollNumber}</td>
                              <td className="p-3 text-sm font-medium">{s.name}</td>
                              <td className="p-3">
                                <input
                                  type="number"
                                  value={markVal ?? ''}
                                  onChange={(e) => updateMark(i, 'marksObtained', e.target.value)}
                                  className={`w-24 border rounded px-2 py-1 text-sm ${isEmpty ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                                  min="0" max={selectedExam.totalMarks}
                                  disabled={!isEditable}
                                  placeholder="Enter"
                                />
                              </td>
                              <td className="p-3 text-sm font-bold">
                                {gradeInfo ? <span className={gradeInfo.color}>{gradeInfo.grade}</span> : '-'}
                              </td>
                              <td className="p-3 text-sm">
                                {hasMarks
                                  ? isPassed
                                    ? <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs font-medium">PASS</span>
                                    : <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs font-medium">FAIL</span>
                                  : '-'}
                              </td>
                              <td className="p-3">
                                <input
                                  type="text"
                                  value={marksData[i]?.remarks ?? ''}
                                  onChange={(e) => updateMark(i, 'remarks', e.target.value)}
                                  className="w-32 border rounded px-2 py-1 text-sm"
                                  placeholder="Optional"
                                  disabled={!isEditable}
                                />
                              </td>
                              <td className="p-3">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(s.status)}`}>
                                  {s.status}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {students.length === 0 && (
                      <p className="text-center py-8 text-gray-400">No students found in this class</p>
                    )}
                  </div>

                  {students.length > 0 && (
                    <div className="flex justify-between items-center mt-6 pt-4 border-t">
                      <button onClick={() => { setActiveTab('exams'); setSelectedExam(null); }}
                        className="text-gray-600 hover:text-gray-800 font-medium">
                        Back to Exams
                      </button>
                      <div className="flex gap-3">
                        <button onClick={handleSaveMarks} disabled={loading || !allMarksEntered()}
                          className={`px-6 py-2 rounded-lg text-sm font-medium transition-colors ${allMarksEntered() ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}>
                          Save Draft ({marksData.filter(m => m.marksObtained !== '' && m.marksObtained !== undefined).length}/{students.length})
                        </button>
                        <button onClick={handleSubmitForReview}
                          className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 text-sm font-medium">
                          Submit for Review
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Exams;